class CollectionInfiniteScroll {
  constructor(element) {
    this.element = element;
    this.status = element.querySelector('.collection-infinite-scroll__status');
    this.loading = false;
    this.onIntersect = this.onIntersect.bind(this);
    this.onCollectionUpdated = this.onCollectionUpdated.bind(this);
    this.onScroll = this.onScroll.bind(this);

    if (!('IntersectionObserver' in window)) {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll);
      this.sync();
      return;
    }

    this.observer = new IntersectionObserver(this.onIntersect, {
      rootMargin: '500px 0px',
    });

    this.observer.observe(this.element);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    window.addEventListener('resize', this.onScroll);
    document.addEventListener('collection:updated', this.onCollectionUpdated);
    this.sync();
  }

  sync() {
    this.productGridContainer = document.getElementById('ProductGridContainer');
    this.productGrid = document.getElementById('product-grid');
    this.paginationContainer = this.productGridContainer
      ? this.productGridContainer.querySelector('.collection-pagination')
      : null;
    this.paginationWrapper = this.productGridContainer
      ? this.productGridContainer.querySelector('.pagination-wrapper')
      : null;
    this.nextLink = this.paginationWrapper ? this.paginationWrapper.querySelector('.next-page-btn a') : null;
    this.complete = this.nextLink === null;

    if (this.paginationContainer && document.documentElement.classList.contains('js')) {
      this.paginationContainer.setAttribute('hidden', 'hidden');
      this.paginationContainer.setAttribute('aria-hidden', 'true');
    }

    this.element.classList.toggle('is-ready', this.complete === false);
    this.element.classList.toggle('is-complete', this.complete);

    this.setStatus('');
    this.scheduleScrollCheck();
  }

  scheduleScrollCheck() {
    if (this.complete || this.scrollCheckScheduled) {
      return;
    }

    this.scrollCheckScheduled = true;
    const callback = () => {
      this.scrollCheckScheduled = false;
      this.onScroll();
    };

    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(callback);
    } else {
      window.setTimeout(callback, 0);
    }
  }

  onCollectionUpdated() {
    this.loading = false;
    this.element.classList.remove('is-loading');
    this.sync();
  }

  onIntersect(entries) {
    if (entries.some((entry) => entry.isIntersecting)) {
      this.loadNextPage();
    }
  }

  onScroll() {
    const remainingScroll = document.documentElement.scrollHeight - (window.innerHeight + window.scrollY);

    if (remainingScroll <= 500) {
      this.loadNextPage();
    }
  }

  async loadNextPage() {
    if (this.loading) {
      return;
    }

    this.loading = true;
    this.sync();

    if (this.complete || !this.nextLink || !this.productGrid) {
      this.loading = false;
      this.element.classList.remove('is-loading');
      this.setStatus(this.complete ? 'All products loaded.' : '');
      return;
    }

    this.element.classList.add('is-loading');
    this.setStatus('Loading more products...');

    try {
      const nextUrl = new URL(this.nextLink.href, window.location.origin);
      const previewThemeId = new URL(window.location.href).searchParams.get('preview_theme_id');

      if (previewThemeId && nextUrl.searchParams.has('preview_theme_id') === false) {
        nextUrl.searchParams.set('preview_theme_id', previewThemeId);
      }

      const response = await fetch(nextUrl.toString(), {
        credentials: 'same-origin',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to load next page: ${response.status}`);
      }

      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = parsed.getElementById('product-grid');

      if (!nextGrid) {
        throw new Error('Next product grid not found');
      }

      const existingHandles = new Set(
        Array.from(this.productGrid.querySelectorAll('[data-product-handle]'))
          .map((el) => el.getAttribute('data-product-handle'))
      );

      const nextItems = Array.from(nextGrid.children).map((child) => document.importNode(child, true));
      nextItems.forEach((child) => {
        const handle = child.getAttribute && child.getAttribute('data-product-handle');
        if (handle && existingHandles.has(handle)) {
          return;
        }
        if (handle) {
          existingHandles.add(handle);
        }
        this.productGrid.appendChild(child);
      });

      const currentPagination = this.productGridContainer.querySelector('.pagination-wrapper');
      const nextPagination = parsed.querySelector('#ProductGridContainer .pagination-wrapper')
        || parsed.querySelector('.pagination-wrapper');

      if (currentPagination && nextPagination) {
        currentPagination.replaceWith(document.importNode(nextPagination, true));
      } else if (currentPagination) {
        currentPagination.remove();
      } else if (nextPagination) {
        this.productGrid.after(document.importNode(nextPagination, true));
      }

      this.loading = false;
      this.element.classList.remove('is-loading');
      this.sync();
      this.setStatus(this.complete ? 'All products loaded.' : '');
    } catch (error) {
      console.error('Infinite scroll failed', error);
      this.loading = false;
      this.element.classList.remove('is-loading');
      this.setStatus('');
    }
  }

  setStatus(message) {
    if (this.status) {
      this.status.textContent = message;
    }
  }
}

const initCollectionInfiniteScroll = () => {
  document.querySelectorAll('.collection-infinite-scroll').forEach((element) => {
    if (!element.__collectionInfiniteScroll) {
      element.__collectionInfiniteScroll = new CollectionInfiniteScroll(element);
    }
  });
};

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionInfiniteScroll);
} else {
  initCollectionInfiniteScroll();
}
